import { useEffect, useState } from 'react';
import { useMutation } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { Alert, Button, Group, Stack, Text, TextInput } from '@mantine/core';
import { notifications } from '@mantine/notifications';
import { IconAlertCircle, IconTruckDelivery } from '@tabler/icons-react';
import { AppModal } from '../../shared/components/AppModal';
import { ApiError } from '../../shared/api/problem';
import { CarrierSelect } from '../shipMethods/CarrierSelect';
import { shipPick, type PickDto } from './api';

interface Props {
  opened: boolean;
  onClose: () => void;
  pick: PickDto;
  /** How many lines still owe something after this goes — what makes it a back order. */
  outstandingLines: number;
  onShipped: () => void;
}

/**
 * Ship confirm.
 *
 * **It says what will happen to the ORDER**, because that is the part people get wrong. Shipping a
 * pick that fell short does not finish the order — it turns it into a back order for the rest, and
 * that is the only route back to being releasable. Saying so here is cheaper than explaining it
 * afterwards.
 *
 * Every field is optional, as legacy's are. A warehouse without a carrier account still ships.
 *
 * **The carrier picker arrived with tenant script 081.** It was left out until then because
 * tbl_ShipMethod had no list procedure and no rows, so the control would have been an empty
 * dropdown — worse than none. It is still optional: a warehouse without a carrier account ships
 * without one, as legacy's nullable ShipVia does.
 */
export function ShipModal({
  opened,
  onClose,
  pick,
  outstandingLines,
  onShipped,
}: Props) {
  const { t } = useTranslation();

  const [tracking, setTracking] = useState('');
  const [bol, setBol] = useState('');
  const [shipMethodId, setShipMethodId] = useState<string | null>(null);

  useEffect(() => {
    if (!opened) return;
    setTracking(pick.trackingNumber ?? '');
    setBol(pick.billOfLading ?? '');
    // The order's carrier is the default, which is what it was chosen for.
    setShipMethodId(pick.shipMethodId ?? null);
  }, [opened, pick.trackingNumber, pick.billOfLading, pick.shipMethodId]);

  const mutation = useMutation({
    mutationFn: () =>
      shipPick(pick.pickId, {
        shipMethodId,
        trackingNumber: tracking.trim() || null,
        billOfLading: bol.trim() || null,
      }),
    onSuccess: () => {
      notifications.show({
        color: 'green',
        message:
          outstandingLines > 0
            ? t('picks.shippedBackOrder')
            : t('picks.shippedComplete'),
      });
      onShipped();
      onClose();
    },
    onError: (error: Error) => {
      notifications.show({
        color: 'red',
        title: t('picks.errors.shipFailed'),
        message:
          error instanceof ApiError ? (error.problem.detail ?? error.message) : error.message,
      });
    },
  });

  return (
    <AppModal opened={opened} onClose={onClose} title={t('picks.shipTitle')} width="md">
      <Stack gap="md">
        <Alert
          icon={<IconTruckDelivery size={16} />}
          color={outstandingLines > 0 ? 'orange' : 'blue'}
          variant="light"
        >
          {outstandingLines > 0
            ? t('picks.shipWillBackOrder', { count: outstandingLines })
            : t('picks.shipWillComplete')}
        </Alert>

        <Text size="sm" c="dimmed">
          {t('picks.shipFrom', { location: pick.pickStagingLocationCode ?? '—' })}
        </Text>

        <CarrierSelect value={shipMethodId} onChange={setShipMethodId} />

        <TextInput
          label={t('picks.tracking')}
          value={tracking}
          onChange={(e) => setTracking(e.currentTarget.value)}
          maxLength={255}
        />

        <TextInput
          label={t('picks.bol')}
          description={t('picks.bolHint')}
          value={bol}
          onChange={(e) => setBol(e.currentTarget.value)}
          maxLength={30}
        />

        {!pick.hasStagedStock && (
          <Alert icon={<IconAlertCircle size={16} />} color="red" variant="light">
            {t('picks.nothingStaged')}
          </Alert>
        )}

        <Group justify="flex-end" gap="sm">
          <Button variant="default" onClick={onClose}>
            {t('shell.cancel')}
          </Button>
          <Button
            color="invetrak"
            leftSection={<IconTruckDelivery size={16} />}
            loading={mutation.isPending}
            disabled={!pick.hasStagedStock}
            onClick={() => mutation.mutate()}
          >
            {t('picks.confirmShipment')}
          </Button>
        </Group>
      </Stack>
    </AppModal>
  );
}
